<script setup>
import { computed } from "vue";

const props = defineProps({
  id: { type: String, required: true },
  modelValue: { type: String, default: "" },
  label: { type: String, required: true },
  type: { type: String, default: "text" },
  inputmode: { type: String, default: "text" },
  autocomplete: { type: String, default: "on" },
  placeholder: { type: String, default: "" },
  error: { type: String, default: "" },
  required: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "blur", "focus"]);

const describedBy = computed(() =>
  props.error ? `${props.id}-error` : undefined
);
</script>

<template>
  <div class="field" :class="{ 'field--error': error }">
    <label class="field__label" :for="id">
      {{ label }}
      <span v-if="!required" class="field__optional">(אופציונלי)</span>
    </label>

    <input
      :id="id"
      class="field__input"
      :type="type"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :value="modelValue"
      :aria-invalid="error ? 'true' : 'false'"
      :aria-describedby="describedBy"
      :required="required"
      @input="emit('update:modelValue', $event.target.value)"
      @blur="emit('blur')"
      @focus="emit('focus')"
    />

    <p v-if="error" :id="`${id}-error`" class="field__error" role="alert">
      {{ error }}
    </p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.field__label {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

.field__optional {
  font-weight: var(--weight-normal);
  color: var(--ink-subtle);
}

.field__input {
  inline-size: 100%;
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-base);
  color: var(--ink);
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  transition: border-color var(--dur-fast) var(--ease-in-out),
    box-shadow var(--dur-fast) var(--ease-in-out);
}

.field__input::placeholder {
  color: var(--ink-subtle);
}

.field__input:hover {
  border-color: var(--ink-subtle);
}

.field__input:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: var(--focus-ring);
}

.field--error .field__input {
  border-color: var(--danger);
}

.field--error .field__input:focus {
  box-shadow: 0 0 0 3px rgba(217, 48, 37, 0.25);
}

.field__error {
  font-size: var(--text-sm);
  color: var(--danger);
}
</style>
